import { useNavigate } from 'react-router-dom'
import {
  type CaseOverviewSlots,
  CaseOverviewView,
} from '@/components/case-overview/CaseOverviewView'
import { useCaseAsOf } from '@/features/case-as-of/CaseAsOf'
import { dashboardState } from '@/features/dashboard/dashboard-state'
import { useCaseDashboard } from '@/features/dashboard/useCaseDashboard'
import { useCaseSettings } from '@/hooks/use-case-queries'
import { useCaseContext } from '@/pages/cases/CaseContext'
import { CaseDescription } from './CaseDescription'
import { CaseJourneyStrip } from './CaseJourneyStrip'
import { CaseOverviewActions } from './CaseOverviewActions'
import { CaseStatusPicker } from './CaseStatusPicker'
import { EntityResolutionCard } from './EntityResolutionCard'
import { SavedInvestigationsCard } from './SavedInvestigationsCard'
import { SpectreAgentCard } from './SpectreAgentCard'

function useOverviewSlots(caseId: string): CaseOverviewSlots {
  const { caseData } = useCaseContext()
  const { data: settings } = useCaseSettings(caseId)
  return {
    status: <CaseStatusPicker caseId={caseId} status={caseData.status} />,
    actions: <CaseOverviewActions caseId={caseId} />,
    assistant: <SpectreAgentCard caseId={caseId} />,
    journey:
      (settings?.show_case_roadmap ?? true) ? (
        <CaseJourneyStrip
          caseId={caseId}
          hasDescription={Boolean(caseData.description?.trim())}
          documentCount={caseData.document_count}
        />
      ) : null,
    about: {
      description: <CaseDescription caseId={caseId} description={caseData.description} />,
      investigations: <SavedInvestigationsCard caseId={caseId} />,
      entityResolution: <EntityResolutionCard caseId={caseId} />,
    },
  }
}

/** The agreed overview, filled with the file as of the URL's date and the current case tools. */
export function CaseOverview({ caseId }: { caseId: string }) {
  const { day, asOf, setDay } = useCaseAsOf()
  const query = useCaseDashboard(caseId, asOf)
  const slots = useOverviewSlots(caseId)
  const navigate = useNavigate()
  return (
    <CaseOverviewView
      dashboard={{
        state: dashboardState(query),
        onRetry: () => void query.refetch(),
        asOf: { day, onChange: setDay },
      }}
      slots={slots}
      onUpload={() => navigate(`/cases/${caseId}/documents?upload=1`)}
    />
  )
}
